import { useEffect, useState } from 'react';
import { Button, Modal } from '@scripsteam/scrips-signal-ds';
import { TextField } from '@scripsteam/scrips-signal-ds';
import { Switch } from '@scripsteam/scrips-signal-ds';
import { FieldLabel } from '@scripsteam/scrips-signal-ds';
import { useSaveCalendarSettings } from '@/settings/services';
import type { CalendarSettings } from '@/types/settings-api';

/**
 * Per-practice Calendar Settings edit dialog — PROD-762.
 *
 * Flutter parity: `edit_calendar_settings_widget.dart`. Edits a SINGLE
 * practice's entry, then submits the FULL practitioner-scoped list to
 * `POST /api/Doctor/CalenderProfile` (typo verbatim) with `isUpdated`
 * flipped to true on the edited row and false on the rest. Server
 * reads `isUpdated` to decide which rows to write through.
 *
 * The list-level submission shape is intentional: backend treats this
 * as a "save the practitioner's schedule snapshot" operation, not a
 * row-level PATCH. Matches Flutter `EnableProviderScheduleEvent`
 * dispatch — caller assembles the full list with `isUpdated: true`
 * only on changed rows.
 *
 * Field set mirrors Flutter `edit_calendar_settings_widget.dart`:
 *   - Default calendar increment (slot length in minutes)
 *   - Show available slots to patient — days ahead
 *   - Show available slots to practice — days ahead
 *   - Overbooking per slot
 *   - Allow virtual appointments (switch)
 *   - Allow home visits (switch); when on, exposes:
 *       - Home visits duration (minutes added per visit)
 *       - Max distance (km)
 */
export interface CalendarSettingsEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The row being edited (one practice's settings). */
  initial: CalendarSettings;
  /** The full list of CalendarSettings the practitioner currently has —
   *  needed because the POST submits the whole array, not a single row. */
  allSettings: CalendarSettings[];
  doctorSetupId: string;
  organizationId: string;
  /** Display name for the practice header. */
  practiceName?: string;
}

export function CalendarSettingsEditDialog({
  open,
  onOpenChange,
  initial,
  allSettings,
  doctorSetupId,
  organizationId,
  practiceName,
}: CalendarSettingsEditDialogProps) {
  const [draft, setDraft] = useState<CalendarSettings>(initial);

  useEffect(() => {
    if (open) setDraft(initial);
  }, [open, initial]);

  const saveMutation = useSaveCalendarSettings(doctorSetupId, organizationId);

  const handleSave = () => {
    // Submit the FULL list. The edited row carries isUpdated: true so
    // the backend knows which row to write through; all others stay at
    // isUpdated: false (server preserves them unchanged).
    const body = allSettings.map((entry) =>
      entry.id === draft.id
        ? { ...draft, isUpdated: true }
        : { ...entry, isUpdated: false },
    );
    saveMutation.mutate(body, {
      onSuccess: () => onOpenChange(false),
    });
  };

  const setField = <K extends keyof CalendarSettings>(
    key: K,
    value: CalendarSettings[K],
  ) => setDraft({ ...draft, [key]: value });

  return (
    <Modal
      open={open}
      onClose={() => onOpenChange(false)}
      title={<>Edit Calendar Settings
            {practiceName ? ` — ${practiceName}` : ''}</>}
      size="lg"
      footer={<><Button
            type="button"
            variant="secondary"
            onClick={() => onOpenChange(false)}
            disabled={saveMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            type="button"
            disabled={saveMutation.isPending}
            onClick={handleSave}
          >
            {saveMutation.isPending ? 'Saving…' : 'Save'}
          </Button></>}
    >
        

        <div className="space-y-4 py-2 max-h-overlay-body overflow-y-auto scrollbar-hide">
          <div className="grid grid-cols-2 gap-4">
            <NumberField
              label="Slot increment (min)"
              value={draft.defaultCalendarIncrement}
              min={1}
              max={120}
              onChange={(v) => setField('defaultCalendarIncrement', v)}
            />
            <NumberField
              label="Overbooking per slot"
              value={draft.overBookingPerSlot}
              min={0}
              onChange={(v) => setField('overBookingPerSlot', v)}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <NumberField
              label="Patient days ahead"
              value={draft.showAvailableSlotsToPatientDaysAhead}
              min={1}
              max={365}
              onChange={(v) =>
                setField('showAvailableSlotsToPatientDaysAhead', v)
              }
            />
            <NumberField
              label="Practice days ahead"
              value={draft.showAvailableSlotsToPracticeDaysAhead}
              min={1}
              max={365}
              onChange={(v) =>
                setField('showAvailableSlotsToPracticeDaysAhead', v)
              }
            />
          </div>

          <div className="border-t border-border-hairline pt-4 space-y-3">
            <ToggleRow
              label="Allow virtual appointments"
              checked={draft.allowVirtualAppointments}
              onChange={(v) => setField('allowVirtualAppointments', v)}
            />
            <ToggleRow
              label="Allow home visits"
              checked={draft.allowHomeVisits}
              onChange={(v) => setField('allowHomeVisits', v)}
            />
            {draft.allowHomeVisits && (
              <div className="grid grid-cols-2 gap-4 pl-1">
                <NumberField
                  label="Home visit duration (min)"
                  value={draft.homeVisitsDuration}
                  min={0}
                  onChange={(v) => setField('homeVisitsDuration', v)}
                />
                <NumberField
                  label="Max distance (km)"
                  value={draft.maxDistance}
                  min={0}
                  onChange={(v) => setField('maxDistance', v)}
                />
              </div>
            )}
          </div>
        </div>

        
      </Modal>
  );
}

function NumberField({
  label,
  value,
  min,
  max,
  onChange,
}: {
  label: string;
  value: number;
  min?: number;
  max?: number;
  onChange: (v: number) => void;
}) {
  return (
    <div>
      <FieldLabel stacked>{label}</FieldLabel>
      <TextField
        type="number"
        value={value}
        min={min}
        max={max}
        onChange={(e) => {
          // Empty input → 0 (Flutter's parseInt parity).
          const next = e.target.value === '' ? 0 : Number(e.target.value);
          if (Number.isFinite(next)) onChange(next);
        }}
        hideSpinButtons
      />
    </div>
  );
}

function ToggleRow({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-sm text-text-default">{label}</span>
      <Switch checked={checked} onCheckedChange={onChange} aria-label={label} />
    </div>
  );
}
